import React, { useState } from "react";
import { Button, Typography, Grid, Menu, MenuItem } from '@mui/material';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { materialsymbolsdownload, wg_report_logo } from 'images';
import dayjs from 'dayjs';
import ExcelJS from "exceljs";
import { saveAs } from 'file-saver';


const FirmDownload = ({ data, selectedDate }) => {
    const [anchorEl, setAnchorEl] = useState(null);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const convertToBase64 = (blob) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result.split(",")[1]);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });

   

    const handleDownload = async (format) => {
        try {
            if (format === 'xlsx') {
                const workbook = new ExcelJS.Workbook();
                const worksheet = workbook.addWorksheet("Summary Adjustment Activity Firm");

                // --- Add WGL Logo ---
                const response = await fetch(wg_report_logo); // assume logo URL is defined
                const imageBlob = await response.blob();
                const base64 = await convertToBase64(imageBlob);
                const imageId = workbook.addImage({
                    base64: base64,
                    extension: "png",
                });

                // Report Header Info
                const reportName = "Summary Adjustment Activity Firm";
                const reportMonth = `For the Month: ${dayjs(selectedDate).format("MMMM YYYY")}`;
                const datePublished = `Date Published: ${dayjs().format("MM/DD/YYYY")}`;

                // Merge A1 and B1
                worksheet.mergeCells("A1:B1");

                // Set the title in the merged cell
                const titleCell = worksheet.getCell("A1");
                titleCell.value = reportName;
                titleCell.font = { size: 16, bold: true };
                titleCell.alignment = { vertical: "middle", horizontal: "left" };

                // Set height for the first row
                worksheet.getRow(1).height = 60;

                // Add logo on the right side (adjust col if needed)
                worksheet.addImage(imageId, {
                    tl: { col: 3, row: 0 }, // Place in row 1, around column I (0-indexed)
                    ext: { width: 150, height: 50 },
                });

                // Report Month (Row 2) — left-aligned
                worksheet.mergeCells("A2:E2");
                const monthCell = worksheet.getCell("A2");
                monthCell.value = reportMonth;
                monthCell.font = { bold: true };
                monthCell.alignment = { horizontal: "left" };

                // Date Published (Row 3) — left-aligned
                worksheet.mergeCells("A3:E3");
                const dateCell = worksheet.getCell("A3");
                dateCell.value = datePublished;
                dateCell.font = { bold: true };
                dateCell.alignment = { horizontal: "left" };



                // Table Headers
                const headers = [
                    "Firm Group Name",
                    "Monthly Group Imbalance",
                    "Monthly Imbalance Adjustment",
                    "Ending Monthly Inventory Balance"
                ];
                const headerRow = worksheet.addRow(headers);
                headerRow.eachCell((cell) => {
                    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
                    cell.fill = {
                        type: "pattern",
                        pattern: "solid",
                        fgColor: { argb: "FF0A2849" },
                    };
                    cell.alignment = { horizontal: "center" };
                });

                // Data Rows
                data.forEach(item => {
                    const row = worksheet.addRow([
                        item.AllocationGroup,
                        item.PreviousBalanceFirm,
                        item.ImbalanceAdjustedVolume,
                        item.MonthEndImbalanceFirm
                    ]);
                    row.eachCell((cell, colNumber) => {
                        cell.alignment = { horizontal: colNumber === 1 ? "center" : "right" };
                        if (colNumber > 1) {
                            cell.numFmt = '#,##0';
                        }
                    });
                });

                // Auto column widths
                worksheet.columns.forEach(column => {
                    let maxLength = 10;
                    column.eachCell({ includeEmpty: true }, cell => {
                        const value = cell.value ? cell.value.toString() : "";
                        maxLength = Math.max(maxLength, value.length);
                    });
                    column.width = maxLength + 2;
                });

                // Export Excel
                const buffer = await workbook.xlsx.writeBuffer();
                const blob = new Blob([buffer], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                });
                saveAs(blob, "Summary_Adjustment_Activity_Firm.xlsx");

            } else if (format === 'pdf') {
                const doc = new jsPDF();
                const pageWidth = doc.internal.pageSize.getWidth();

                // === Title ===
                doc.setFontSize(16);
                doc.text("Summary Adjustment Activity Firm", 14, 15);

                // === Report Metadata ===
                doc.setFontSize(11);
                doc.text(`For the Month: ${dayjs(selectedDate).format("MMMM YYYY")}`, 14, 25);
                doc.text(`Date Published: ${dayjs().format("MM/DD/YYYY")}`, 14, 32);

                // === WGL Logo (top-right corner) ===
                const imgWidth = 40;
                const imgHeight = 15;
                const imgX = pageWidth - imgWidth - 14;
                const imgY = 10;

                const logoImg = new Image();
                logoImg.src = wg_report_logo; // assumes this is defined

                logoImg.onload = () => {
                    doc.addImage(logoImg, 'PNG', imgX, imgY, imgWidth, imgHeight);

                    // === Table ===
                    autoTable(doc, {
                        startY: 40,
                        head: [[
                            'Firm Group Name',
                            'Monthly Group Imbalance',
                            'Monthly Imbalance Adjustment',
                            'Ending Monthly Inventory Balance'
                        ]],
                        body: data.map(item => [
                            item.AllocationGroup,
                            item?.PreviousBalanceFirm?.toLocaleString() || 0,
                            item?.ImbalanceAdjustedVolume?.toLocaleString() || 0,
                            item?.MonthEndImbalanceFirm?.toLocaleString() || 0
                        ]),
                        styles: {
                            halign: 'center', // default alignment
                            valign: 'middle',
                            fontSize: 10,
                        },
                        headStyles: {
                            fillColor: [10, 40, 73], // Dark blue background
                            textColor: [255, 255, 255], // White text
                            halign: 'center',
                            fontStyle: 'bold',
                        },
                        columnStyles: {
                            1: { halign: 'right' },
                            2: { halign: 'right' },
                            3: { halign: 'right' },
                        }
                    });

                    doc.save("Summary_Adjustment_Activity_Firm.pdf");
                };

            } else if (format === 'csv') {
                const reportName = "Summary Adjustment Activity Firm";
                const reportMonth = `For the Month: ${dayjs(selectedDate).format("MMMM YYYY")}`;
                const datePublished = `Date Published: ${dayjs().format("MM/DD/YYYY")}`;

                // Array of arrays for CSV
                const csvRows = [
                    [reportName],
                    [reportMonth],
                    [datePublished],
                    [], // Spacer
                    ["Firm Group Name", "Monthly Group Imbalance", "Monthly Imbalance Adjustment", "Ending Monthly Inventory Balance"],
                    ...data.map(item => [
                        item.AllocationGroup,
                        item?.PreviousBalanceFirm?.toLocaleString() || 0,  // adds comma formatting
                        item?.ImbalanceAdjustedVolume?.toLocaleString() || 0,
                        item?.MonthEndImbalanceFirm?.toLocaleString() || 0
                    ])
                ];

                const worksheet = XLSX.utils.aoa_to_sheet(csvRows);
                const csvData = XLSX.utils.sheet_to_csv(worksheet);

                const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = "Summary_Adjustment_Activity_Firm.csv";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
            handleClose();

        } catch (error) {
            console.error("Error generating the report:", error);
        }
    };

    return (
        <>
            <Button
                variant="contained"
                className='Download'
                color="primary"
                onClick={handleClick}
                disabled={!data || data?.length === 0}
            >
                <img src={materialsymbolsdownload} alt="Download" /> Download
            </Button>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
            >
                <MenuItem onClick={() => handleDownload('xlsx')}>Excel</MenuItem>
                <MenuItem onClick={() => handleDownload('csv')}>CSV</MenuItem>
                <MenuItem onClick={() => handleDownload('pdf')}>PDF</MenuItem>
            </Menu>
        </>
    );
};

export default FirmDownload;